"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import FormField from "../ui/form-field";
import StateButton from "../ui/state-button";
import {
  IoPerson,
  IoMail,
  IoChatbubble,
  IoArrowForward,
  IoArrowBack,
} from "react-icons/io5";
import { useRouter } from "next/navigation";

interface FormData {
  name: string;
  email: string;
  message: string;
  duration?: string;
}

interface FormErrors {
  name: string;
  email: string;
  message: string;
  duration?: string;
}

type FormStep = "tags" | "duration" | "details";
type SubmitState = "idle" | "submitting" | "success" | "error";

const tags = [
  "MVP",
  "Idea",
  "POC",
  "Web Development",
  "App Development",
  "Product Design",
  "Creatives",
  "Consultation",
  "Careers",
  "Others",
];

const durations = [
  "1 week",
  "2-3 weeks",
  "1 month",
  "2-3 months",
  "3+ months",
  "Not sure yet",
];

const directSubmitTags = ["Consultation", "Careers"];

export default function ContactForm() {
  const [currentStep, setCurrentStep] = useState<FormStep>("tags");
  const [selectedTag, setSelectedTag] = useState<string>("");
  const [selectedDuration, setSelectedDuration] = useState<string>("");
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({
    name: "",
    email: "",
    message: "",
  });
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const router = useRouter();
  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (field: string, value: string): string => {
    switch (field) {
      case "name":
        if (!value.trim()) return "Name is required";
        if (value.trim().length < 2)
          return "Name must be at least 2 characters";
        if (value.trim().length > 50)
          return "Name must be less than 50 characters";
        if (!/^[a-zA-Z\s]+$/.test(value.trim()))
          return "Name can only contain letters and spaces";
        return "";

      case "email":
        if (!value.trim()) return "Email is required";
        if (!validateEmail(value.trim()))
          return "Please enter a valid email address";
        return "";

      case "message":
        if (!value.trim()) return "Message is required";
        if (value.trim().length < 10)
          return "Message must be at least 10 characters";
        if (value.trim().length > 1000)
          return "Message must be less than 1000 characters";
        return "";

      default:
        return "";
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleInputBlur = (field: string, value: string) => {
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleTagSelect = (tag: string) => {
    setSelectedTag(tag);
    if (directSubmitTags.includes(tag)) {
      setCurrentStep("details");
    } else {
      setCurrentStep("duration");
    }
  };

  const handleDurationSelect = (duration: string) => {
    setSelectedDuration(duration);
    setCurrentStep("details");
  };

  const handleBack = () => {
    if (currentStep === "details") {
      if (directSubmitTags.includes(selectedTag)) {
        setCurrentStep("tags");
        setSelectedTag("");
      } else {
        setCurrentStep("duration");
        setSelectedDuration("");
      }
    } else if (currentStep === "duration") {
      setCurrentStep("tags");
      setSelectedTag("");
    }
  };

  const sendEmail = async (emailContent: string) => {
    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          to: "huma9016@gmail.com",
          subject: `New Contact Form Submission - ${selectedTag}`,
          content: emailContent,
          from: formData.email,
          name: formData.name,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      return await response.json();
    } catch (error) {
      console.error("Email sending error:", error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const nameError = validateField("name", formData.name);
    const emailError = validateField("email", formData.email);
    const messageError = validateField("message", formData.message);

    setErrors({
      name: nameError,
      email: emailError,
      message: messageError,
    });

    if (nameError || emailError || messageError) {
      return;
    }

    setSubmitState("submitting");

    try {
      const emailContent = `
        New Contact Form Submission

        Category: ${selectedTag}
        ${selectedDuration ? `Duration: ${selectedDuration}` : ""}

        Name: ${formData.name}
        Email: ${formData.email}
        Message: ${formData.message}

        This submission was made from the ZySoftec contact form.
      `;

      await sendEmail(emailContent);

      setSubmitState("success");
      toast.success("Message sent successfully! We'll get back to you soon.");

      // Reset form after success
      setTimeout(() => {
        setFormData({ name: "", email: "", message: "" });
        setErrors({ name: "", email: "", message: "" });
        setSelectedTag("");
        setSelectedDuration("");
        setCurrentStep("tags");
        setSubmitState("idle");
      }, 2000);

      router.push("/");
    } catch (error) {
      setSubmitState("error");
      toast.error("Failed to send message. Please try again.");

      setTimeout(() => {
        setSubmitState("idle");
      }, 3000);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "tags":
        return (
          <motion.div
            key="tags"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                What can we help you with?
              </h3>
              <p className="text-gray-600">
                Select the category that best describes your project or inquiry.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagSelect(tag)}
                  className="p-4 text-left border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all duration-200 group cursor-pointer"
                >
                  <div className="font-medium text-gray-900 group-hover:text-primary transition-colors">
                    {tag.charAt(0).toUpperCase() + tag.slice(1)}
                  </div>
                  {/* <div className="text-sm text-gray-500 mt-1">
                    {directSubmitTags.includes(tag)
                      ? "Direct contact"
                      : "Project inquiry"}
                  </div> */}
                </button>
              ))}
            </div>
          </motion.div>
        );

      case "duration":
        return (
          <motion.div
            key="duration"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                What's your timeline?
              </h3>
              <p className="text-gray-600">
                This helps us understand your project urgency and plan
                accordingly.
              </p>
            </div>

            <div className="space-y-3">
              {durations.map((duration) => (
                <button
                  key={duration}
                  onClick={() => handleDurationSelect(duration)}
                  className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all duration-200 group cursor-pointer"
                >
                  <div className="font-medium text-gray-900 group-hover:text-primary transition-colors">
                    {duration}
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        );

      case "details":
        return (
          <motion.div
            key="details"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Tell us more
              </h3>
              <p className="text-gray-600">
                Share your details and we'll get back to you with a personalized
                response.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField
                type="text"
                label="Full Name"
                value={formData.name}
                onChange={(value) => handleInputChange("name", value)}
                onBlur={(value) => handleInputBlur("name", value)}
                error={errors.name}
                placeholder="Enter your full name"
                required
                icon={IoPerson}
              />

              <FormField
                type="email"
                label="Email Address"
                value={formData.email}
                onChange={(value) => handleInputChange("email", value)}
                onBlur={(value) => handleInputBlur("email", value)}
                error={errors.email}
                placeholder="Enter your email address"
                required
                icon={IoMail}
              />

              <FormField
                type="textarea"
                label="Message"
                value={formData.message}
                onChange={(value) => handleInputChange("message", value)}
                onBlur={(value) => handleInputBlur("message", value)}
                error={errors.message}
                placeholder="Tell us about your project or inquiry..."
                required
                icon={IoChatbubble}
              />

              <StateButton
                state={submitState}
                disabled={
                  !formData.name ||
                  !formData.email ||
                  !formData.message ||
                  !!errors.name ||
                  !!errors.email ||
                  !!errors.message
                }
                successText="Message Sent!"
                errorText="Failed to Send"
                submittingText="Sending..."
              >
                Send Message
              </StateButton>
            </form>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-2">
          {currentStep !== "tags" && (
            <button
              onClick={handleBack}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
            >
              <IoArrowBack size={20} />
            </button>
          )}
          <div className="text-sm text-gray-500">
            Step{" "}
            {currentStep === "tags" ? 1 : currentStep === "duration" ? 2 : 3} of
            3
          </div>
        </div>

        <div className="flex space-x-2">
          <div
            className={`w-3 h-3 rounded-full transition-colors ${
              currentStep === "tags" ? "bg-primary" : "bg-gray-300"
            }`}
          />
          <div
            className={`w-3 h-3 rounded-full transition-colors ${
              currentStep === "duration" ? "bg-primary" : "bg-gray-300"
            }`}
          />
          <div
            className={`w-3 h-3 rounded-full transition-colors ${
              currentStep === "details" ? "bg-primary" : "bg-gray-300"
            }`}
          />
        </div>
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">{renderStepContent()}</AnimatePresence>
    </div>
  );
}
